import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, LogOut, User, Settings, Home, Bell, Search, Menu, X, BarChart3 } from 'lucide-react';
import { authService } from '@/lib/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from '@/components/ThemeToggle';
import { useState, useEffect } from 'react';

interface LayoutProps {
  children: ReactNode;
  title: string;
}

export default function Layout({ children, title }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [notifications] = useState(3); // Mock notification count

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
  authService.logout();
  navigate('/login');
};


  const navItems = currentUser ? [
    ...(currentUser.role === 'admin' 
      ? [
          { path: '/admin', label: 'Dashboard', icon: <BarChart3 className="h-4 w-4" /> },
          { path: '/report', label: 'Report Issue' },
        ]
      : [
          { path: '/', label: 'My Reports', icon: <Home className="h-4 w-4" /> },
          { path: '/report', label: 'Report Issue' },
        ]
    )
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors">
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-lg' 
          : 'bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500 rounded-lg blur opacity-20"></div>
                <MapPin className="relative h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Street Issue Reporter</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Real-time civic reporting system</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            {currentUser && (
              <div className="hidden md:flex items-center space-x-4">
                {/* User Info */}
                <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 rounded-full">
                  <div className="flex items-center space-x-2">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {currentUser.name}
                      </span>
                      <div className="flex items-center space-x-1">
                        {currentUser.role === 'admin' && (
                          <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                            Admin
                          </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {currentUser.email}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-800/50 p-1 rounded-full">
                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      variant={location.pathname === item.path ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => navigate(item.path)}
                      className={`rounded-full ${
                        location.pathname === item.path 
                          ? 'shadow-md' 
                          : 'hover:bg-white dark:hover:bg-gray-700'
                      }`}
                    >
                      {item.icon && <span className="mr-2">{item.icon}</span>}
                      {item.label}
                    </Button>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  {/* Search Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0"
                    onClick={() => navigate('/search')}
                  >
                    <Search className="h-4 w-4" />
                  </Button>

                  {/* Notifications */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0 relative"
                    onClick={() => navigate('/notifications')}
                  >
                    <Bell className="h-4 w-4" />
                    {notifications > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {notifications}
                      </span>
                    )}
                  </Button>

                  {/* Theme Toggle */}
                  <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-full">
                    <ThemeToggle />
                  </div>

                  {/* Logout */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={handleLogout}
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-2">
              {currentUser && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0 relative"
                    onClick={() => navigate('/notifications')}
                  >
                    <Bell className="h-4 w-4" />
                    {notifications > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {notifications}
                      </span>
                    )}
                  </Button>
                  <ThemeToggle />
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="rounded-full h-9 w-9 p-0"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && currentUser && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 animate-in slide-in-from-top">
            <div className="px-4 py-3 space-y-3">
              {/* User Info Mobile */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {currentUser.name}
                    </span>
                    {currentUser.role === 'admin' && (
                      <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                        Admin
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {currentUser.email}
                  </p>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="space-y-1">
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    variant={location.pathname === item.path ? 'default' : 'ghost'}
                    size="lg"
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    {item.icon && <span className="mr-3">{item.icon}</span>}
                    {item.label}
                  </Button>
                ))}
              </div>

              {/* Mobile Actions */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="ghost"
                  size="lg"
                  className="w-full justify-start text-red-500"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {title}
              </h2>
              {currentUser?.role === 'admin' && (
                <div className="flex items-center gap-2">
                  <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Settings className="h-3 w-3" />
                    Admin View
                  </span>
                  <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <BarChart3 className="h-3 w-3" />
                    Analytics Enabled
                  </span>
                </div>
              )}
            </div>
            
            {/* Quick Stats (Optional Enhancement) */}
            {currentUser?.role === 'admin' && (
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">42</div>
                  <div className="text-gray-500 dark:text-gray-400">Reports Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">86%</div>
                  <div className="text-gray-500 dark:text-gray-400">Resolved</div>
                </div>
              </div>
            )}
          </div>
          
          {/* Breadcrumb Navigation (Optional Enhancement) */}
          <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
            <button
              onClick={() => navigate('/')}
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Home
            </button>
            <span className="mx-2">/</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {title}
            </span>
          </div>
        </div>

        {/* Content Area with Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in fade-in duration-300">
          {children}
        </div>

        {/* Footer (Optional Enhancement) */}
        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Street Issue Reporter System v1.0
              </span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => navigate('/help')}
                className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Help Center
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
            © {new Date().getFullYear()} Civic Tech Platform. All rights reserved.
          </p>
        </footer>
      </main>
    </div>
  );
}